import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, UserPlus, UserCheck, Clock, Share2, Edit, Lock, Globe, Pin, Heart, MessageCircle, MessageSquare, X, Sparkles, BarChart3, MoreVertical, Trash2, Pencil, Bookmark, Flame, EyeOff } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import PostCard from '@/components/PostCard';
import GlowBadge from '@/components/GlowBadge';

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [followStatus, setFollowStatus] = useState(null); // 'following', 'requested', 'none'
  const [followLoading, setFollowLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [userLooks, setUserLooks] = useState([]);
  const [savedLooks, setSavedLooks] = useState([]);
  const [lookMenuOpen, setLookMenuOpen] = useState(null);
  const [editingLook, setEditingLook] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [deletingLook, setDeletingLook] = useState(null);
  const [showFollowModal, setShowFollowModal] = useState(null); // 'followers' | 'following' | null
  const [followList, setFollowList] = useState([]);
  const [followListLoading, setFollowListLoading] = useState(false);
  const [streakData, setStreakData] = useState(null);
  const [showStreakPopup, setShowStreakPopup] = useState(false);

  // Define useCallback functions BEFORE useEffect hooks that use them
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  }, []);

  const fetchStreakData = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/routines/streak-summary`);
      if (res.ok) {
        const data = await res.json();
        if (data.total_routines > 0) setStreakData(data);
      }
    } catch (e) { console.error('fetchStreakData error:', e); }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchUserLooks = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/users/${userId}/looks`);
      if (response.ok) setUserLooks(await response.json());
    } catch (e) { console.error('Error fetching looks:', e); }
  }, [userId]);

  const fetchSavedLooks = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/looks/saved`);
      if (response.ok) setSavedLooks(await response.json());
    } catch (e) { console.error('Error fetching saved looks:', e); }
  }, []);

  // useEffect hooks that depend on the useCallback functions above
  useEffect(() => {
    fetchCurrentUser();
    fetchProfile();
    fetchUserLooks();
    fetchStreakData();
  }, [userId, fetchCurrentUser, fetchProfile, fetchUserLooks, fetchStreakData]);

  useEffect(() => {
    if (activeTab === 'saved' && currentUser?.user_id === userId) {
      fetchSavedLooks();
    }
  }, [activeTab, currentUser, userId, fetchSavedLooks]);

  const checkFollowStatus = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/users/${userId}/follow-status`, {
      });
      if (response.ok) {
        const data = await response.json();
        setFollowStatus(data.status);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (currentUser && profileData && currentUser.user_id !== userId) {
      checkFollowStatus();
    }
  }, [currentUser, profileData, userId, checkFollowStatus]);

  const handleDeleteLook = async (lookId) => {
    try {
      const response = await authFetch(`${API}/looks/${lookId}`, { method: 'DELETE' });
      if (response.ok) {
        setUserLooks(prev => prev.filter(l => l.look_id !== lookId));
        setDeletingLook(null);
        toast.success('Look deleted');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (e) { toast.error('Failed to delete look'); }
  };

  const handleEditLook = async () => {
    if (!editingLook) return;
    try {
      const response = await authFetch(`${API}/looks/${editingLook.look_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: editCaption })
      });
      if (response.ok) {
        setUserLooks(prev => prev.map(l =>
          l.look_id === editingLook.look_id ? { ...l, caption: editCaption } : l
        ));
        setEditingLook(null);
        toast.success('Look updated');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch (e) { toast.error('Failed to update look'); }
  };

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      const response = await authFetch(`${API}/users/${userId}/follow`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setFollowStatus(data.status);
        toast.success(data.message);
      }
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    setFollowLoading(true);
    try {
      const response = await authFetch(`${API}/users/${userId}/unfollow`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setFollowStatus('none');
        toast.success('Unfollowed');
      }
    } catch (error) {
      console.error('Error unfollowing user:', error);
      toast.error('Failed to unfollow');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await authFetch(`${API}/posts/${postId}/like`, {
        method: 'POST',
      });
      if (response.ok) {
        const result = await response.json();
        const updatePost = (post) => {
          if (post.post_id !== postId) return post;
          const likes = post.likes || [];
          return {
            ...post,
            likes: result.liked 
              ? [...likes, currentUser.user_id]
              : likes.filter(id => id !== currentUser.user_id)
          };
        };
        setProfileData(prev => ({
          ...prev,
          posts: prev.posts.map(updatePost)
        }));
        if (selectedPost?.post_id === postId) {
          setSelectedPost(prev => updatePost(prev));
        }
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/profile/${userId}`;
    const shareText = `Check out @${profileData?.user?.username || profileData?.user?.name?.replace(/\s+/g, '').toLowerCase()}'s profile on Gloverse!`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Gloverse Profile',
          text: shareText,
          url: profileUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(profileUrl);
        }
      }
    } else {
      copyToClipboard(profileUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Profile link copied to clipboard!');
  };

  const openFollowModal = async (type) => {
    setShowFollowModal(type);
    setFollowListLoading(true);
    try {
      const response = await authFetch(`${API}/users/${userId}/${type}`);
      if (response.ok) {
        setFollowList(await response.json());
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
    } finally {
      setFollowListLoading(false);
    }
  };

  const handleFollowBack = async (targetUserId) => {
    try {
      const response = await authFetch(`${API}/users/${targetUserId}/follow`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'requested') {
          // Private account — follow request sent
          setFollowList(prev => prev.map(u =>
            u.user_id === targetUserId ? { ...u, has_pending_request: true } : u
          ));
          toast.success('Follow request sent!');
        } else {
          // Public account — instant follow
          setFollowList(prev => prev.map(u =>
            u.user_id === targetUserId ? { ...u, is_following_back: true } : u
          ));
          setProfileData(prev => ({
            ...prev,
            user: { ...prev.user, following_count: (prev.user.following_count || 0) + 1 }
          }));
          toast.success('Following!');
        }
      }
    } catch (error) {
      toast.error('Failed to follow');
    }
  };

  const handleRemoveFollower = async (followerId) => {
    try {
      const response = await authFetch(`${API}/users/${userId}/followers/${followerId}`, { method: 'DELETE' });
      if (response.ok) {
        setFollowList(prev => prev.filter(u => u.user_id !== followerId));
        setProfileData(prev => ({
          ...prev,
          user: { ...prev.user, followers_count: Math.max(0, (prev.user.followers_count || 0) - 1) }
        }));
        toast.success('Follower removed');
      }
    } catch (error) {
      toast.error('Failed to remove follower');
    }
  };

  const handleUnfollowFromList = async (targetUserId) => {
    try {
      const response = await authFetch(`${API}/users/${targetUserId}/unfollow`, { method: 'DELETE' });
      if (response.ok) {
        setFollowList(prev => prev.filter(u => u.user_id !== targetUserId));
        setProfileData(prev => ({
          ...prev,
          user: { ...prev.user, following_count: Math.max(0, (prev.user.following_count || 0) - 1) }
        }));
        toast.success('Unfollowed');
      }
    } catch (error) {
      toast.error('Failed to unfollow');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) return null;

  const isOwnProfile = currentUser?.user_id === userId;

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            {!isOwnProfile && (
              <button 
                data-testid="profile-message-btn" 
                onClick={async () => {
                  try {
                    const response = await authFetch(`${API}/conversations`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ participant_ids: [userId], is_group: false })
                    });
                    if (response.ok) {
                      const conv = await response.json();
                      navigate(`/messages/${conv.conversation_id}`);
                    }
                  } catch (e) { console.error(e); }
                }}
                className="p-2 hover:bg-primary/10 rounded-full transition-colors"
                title="Message"
              >
                <MessageSquare className="w-6 h-6" />
              </button>
            )}
            <button 
              data-testid="share-profile-btn" 
              onClick={handleShareProfile} 
              className="p-2 hover:bg-primary/10 rounded-full transition-colors"
              title="Share Profile"
            >
              <Share2 className="w-6 h-6" />
            </button>
            {isOwnProfile && (
              <>
                <button 
                  data-testid="edit-profile-btn" 
                  onClick={() => navigate('/edit-profile')} 
                  className="p-2 hover:bg-primary/10 rounded-full transition-colors"
                  title="Edit Profile"
                >
                  <Edit className="w-6 h-6" />
                </button>
                <button 
                  data-testid="settings-btn" 
                  onClick={() => navigate('/settings')} 
                  className="p-2 hover:bg-primary/10 rounded-full transition-colors"
                  title="Settings"
                >
                  <Settings className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center gap-6 mb-6">
          {profileData.user.picture ? (
            <img src={profileData.user.picture} alt={profileData.user.name} className="w-24 h-24 rounded-full border-4 border-primary/20 object-cover" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-3xl font-bold">
              {profileData.user.name?.charAt(0) || 'U'}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {profileData.user.name}
              </h2>
              <GlowBadge tier={profileData.user.glow_badge} size="md" />
              {isOwnProfile && streakData && (
                <button
                  data-testid="streak-icon-btn"
                  onClick={() => setShowStreakPopup(!showStreakPopup)}
                  className="relative group"
                  title="Routine Streak"
                >
                  <Flame className={`w-5 h-5 transition-colors ${streakData.best_streak > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                  {streakData.best_streak > 0 && (
                    <span className="absolute -top-1.5 -right-2 bg-orange-500 text-white text-[9px] font-black min-w-[14px] h-[14px] rounded-full flex items-center justify-center leading-none px-0.5">
                      {streakData.best_streak}
                    </span>
                  )}
                </button>
              )}
              {profileData.user.is_private ? (
                <Lock className="w-4 h-4 text-muted-foreground" title="Private Account" />
              ) : (
                <Globe className="w-4 h-4 text-muted-foreground" title="Public Account" />
              )}
            </div>
            <p className="text-muted-foreground text-sm mb-2">
              @{profileData.user.username || profileData.user.name?.replace(/\s+/g, '').toLowerCase()}
            </p>
            {profileData.user.bio && (
              <p className="text-sm text-foreground mb-3">{profileData.user.bio}</p>
            )}
            <div className="flex gap-6 text-sm">
              <div>
                <span className="font-bold text-foreground">{profileData.posts?.length || 0}</span>
                <span className="text-muted-foreground ml-1">Posts</span>
              </div>
              <button data-testid="followers-count-btn" onClick={() => openFollowModal('followers')} className="hover:opacity-70 transition-opacity">
                <span className="font-bold text-foreground">{profileData.user.followers_count || 0}</span>
                <span className="text-muted-foreground ml-1">Followers</span>
              </button>
              <button data-testid="following-count-btn" onClick={() => openFollowModal('following')} className="hover:opacity-70 transition-opacity">
                <span className="font-bold text-foreground">{profileData.user.following_count || 0}</span>
                <span className="text-muted-foreground ml-1">Following</span>
              </button>
            </div>
          </div>
        </div>

        {/* Creator Insights Button - own profile only */}
        {isOwnProfile && (
          <button
            data-testid="view-insights-btn"
            onClick={() => navigate('/creator-dashboard')}
            className="w-full mb-4 py-2.5 px-4 rounded-xl border border-border bg-white/70 hover:bg-primary/5 hover:border-primary/30 transition-all flex items-center justify-center gap-2 text-sm font-medium text-foreground"
          >
            <BarChart3 className="w-4 h-4 text-primary" />
            View Insights
          </button>
        )}

        {/* Streak Stats Popup */}
        {showStreakPopup && streakData && (
          <div data-testid="streak-popup" className="mb-4 rounded-2xl overflow-hidden bg-gradient-to-br from-orange-50 to-rose-50 border border-orange-200/60 shadow-sm">
            <div className="px-4 py-3 flex items-center justify-between border-b border-orange-200/40">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-semibold text-foreground">Routine Streaks</span>
              </div>
              <button onClick={() => setShowStreakPopup(false)} className="p-1 rounded-full hover:bg-orange-100 transition-colors">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-black text-orange-600">{streakData.best_streak}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Best Streak</p>
              </div>
              <div>
                <p className="text-2xl font-black text-rose-600">{streakData.total_checkins}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Check-ins</p>
              </div>
              <div>
                <p className="text-2xl font-black text-pink-600">{streakData.total_routines}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Routines</p>
              </div>
            </div>
            {streakData.milestones?.length > 0 && (
              <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                {streakData.milestones.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-white/80 border border-orange-200/60 px-2 py-1 rounded-full">
                    <span>{m.icon}</span>
                    <span className="font-medium text-foreground">{m.name}</span>
                  </span>
                ))}
              </div>
            )}
            <button
              data-testid="view-routines-btn"
              onClick={() => navigate('/routines')}
              className="w-full py-2.5 text-center text-xs font-semibold text-orange-600 hover:bg-orange-100/50 transition-colors border-t border-orange-200/40"
            >
              View All Routines
            </button>
          </div>
        )}

        {/* Follow Button for other users */}
        {!isOwnProfile && currentUser && (
          <div className="mb-6">
            {followStatus === 'following' ? (
              <button
                data-testid="unfollow-btn"
                onClick={handleUnfollow}
                disabled={followLoading}
                className="w-full py-3 px-4 rounded-xl border-2 border-border bg-white/50 font-medium flex items-center justify-center gap-2 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
              >
                <UserCheck className="w-5 h-5" />
                {followLoading ? 'Loading...' : 'Following'}
              </button>
            ) : followStatus === 'requested' ? (
              <button
                data-testid="requested-btn"
                onClick={handleUnfollow}
                disabled={followLoading}
                className="w-full py-3 px-4 rounded-xl border-2 border-border bg-white/50 font-medium flex items-center justify-center gap-2 text-muted-foreground hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
              >
                <Clock className="w-5 h-5" />
                {followLoading ? 'Loading...' : 'Requested'}
              </button>
            ) : (
              <button
                data-testid="follow-btn"
                onClick={handleFollow}
                disabled={followLoading}
                className="w-full py-3 px-4 rounded-xl bg-primary text-white font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
              >
                <UserPlus className="w-5 h-5" />
                {followLoading ? 'Loading...' : 'Follow'}
              </button>
            )}
          </div>
        )}

        {/* Tabs & Content - only show if can view or own profile */}
        {(profileData.can_view_content !== false) ? (
          <>
            {/* Tabs */}
            <div className="flex border-b border-border mb-6">
              <button
                data-testid="posts-tab"
                onClick={() => setActiveTab('posts')}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === 'posts' 
                    ? 'text-primary border-b-2 border-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Posts
              </button>
              <button
                data-testid="looks-tab"
                onClick={() => setActiveTab('looks')}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === 'looks' 
                    ? 'text-primary border-b-2 border-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Looks
              </button>
              <button
                data-testid="checklists-tab"
                onClick={() => {
                  if (isOwnProfile) {
                    navigate('/checklists');
                  } else {
                    setActiveTab('checklists');
                  }
                }}
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === 'checklists' 
                    ? 'text-primary border-b-2 border-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Checklists
              </button>
              {isOwnProfile && (
                <button
                  data-testid="saved-tab"
                  onClick={() => setActiveTab('saved')}
                  className={`flex-1 py-3 text-center font-medium transition-colors ${
                    activeTab === 'saved'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Saved
                </button>
              )}
            </div>

            {/* Content */}
        <div>
          {/* Determine if photos should be hidden from this viewer */}
          {(() => {
            const photosHidden = profileData.hide_photos && !isOwnProfile && !profileData.is_following;
            return (
              <>
          <div className={`transition-opacity duration-200 ${activeTab === 'posts' ? 'opacity-100' : 'hidden opacity-0'}`}>
          {activeTab === 'posts' && (
            photosHidden ? (
              <div className="text-center py-16 space-y-3" data-testid="photos-hidden-posts">
                <EyeOff className="w-10 h-10 text-muted-foreground/40 mx-auto" />
                <p className="text-base font-medium text-foreground">Photos are hidden</p>
                <p className="text-sm text-muted-foreground">Follow this user to see their posts and looks.<br/>Their checklists are still public.</p>
              </div>
            ) :
            profileData.posts?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No posts yet</p>
              </div>
            ) : (
              <>
                {/* Grid View for Posts */}
                {(() => {
                  // Separate pinned and unpinned posts
                  const pinnedPosts = profileData.posts?.filter(p => p.is_pinned) || [];
                  const unpinnedPosts = profileData.posts?.filter(p => !p.is_pinned) || [];
                  const sortedPosts = [...pinnedPosts, ...unpinnedPosts];
                  
                  return (
                    <div className="grid grid-cols-3 gap-0.5">
                      {sortedPosts.map(post => {
                        const mediaUrls = post.media_urls || (post.image_url ? [post.image_url] : []);
                        const firstImage = mediaUrls[0];
                        
                        return (
                          <button
                            key={post.post_id}
                            onClick={() => setSelectedPost(post)}
                            className="relative aspect-square overflow-hidden bg-gray-100 group"
                          >
                            {firstImage ? (
                              <img 
                                src={firstImage} 
                                alt="" 
                                className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                <span className="text-gray-400 text-xs">No image</span>
                              </div>
                            )}
                            
                            {/* Pinned indicator */}
                            {post.is_pinned && (
                              <div className="absolute top-1.5 left-1.5 p-1 bg-white/90 rounded-full shadow">
                                <Pin className="w-3 h-3 text-primary fill-primary" />
                              </div>
                            )}
                            
                            {/* Multiple images indicator */}
                            {mediaUrls.length > 1 && (
                              <div className="absolute top-1.5 right-1.5 p-1 bg-black/60 rounded-md">
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H8V4h12v12z"/>
                                </svg>
                              </div>
                            )}
                            
                            {/* Hover overlay with stats */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                              <span className="flex items-center gap-1 text-white font-bold text-sm">
                                <Heart className="w-4 h-4 fill-white" />
                                {post.likes?.length || 0}
                              </span>
                              <span className="flex items-center gap-1 text-white font-bold text-sm">
                                <MessageCircle className="w-4 h-4 fill-white" />
                                {post.comments?.length || 0}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </>
            )
          )}
          </div>

          <div className={`transition-opacity duration-200 ${activeTab === 'looks' ? 'opacity-100' : 'hidden opacity-0'}`}>
          {activeTab === 'looks' && (
            photosHidden ? (
              <div className="text-center py-16 space-y-3" data-testid="photos-hidden-looks">
                <EyeOff className="w-10 h-10 text-muted-foreground/40 mx-auto" />
                <p className="text-base font-medium text-foreground">Looks are hidden</p>
                <p className="text-sm text-muted-foreground">Follow this user to see their looks.<br/>Their checklists are still public.</p>
              </div>
            ) :
            userLooks.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <Sparkles className="w-10 h-10 text-muted-foreground/40 mx-auto" />
                <p className="text-muted-foreground">No looks yet</p>
                {isOwnProfile && (
                  <button onClick={() => navigate('/create-look')} className="px-5 py-2 bg-primary text-white rounded-full text-sm font-medium">
                    Share your first look
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-0.5">
                {userLooks.map(look => {
                  const isBA = look.theme === 'Before & After';
                  const firstImage = isBA ? look.after_image : look.images?.[0];
                  return (
                    <div key={look.look_id} className="relative">
                      <button
                        data-testid={`profile-look-${look.look_id}`}
                        onClick={() => navigate(`/spotlight?look=${look.look_id}`)}
                        className="relative aspect-square overflow-hidden bg-gray-100 group w-full"
                      >
                        {firstImage ? (
                          <img src={firstImage} alt="" className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                          <span className="text-white text-[10px] font-medium">{look.theme}</span>
                        </div>
                        <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 bg-black/50 px-1.5 py-0.5 rounded-full">
                          <Heart className="w-2.5 h-2.5 text-white fill-white" />
                          <span className="text-white text-[10px]">{look.likes?.length || 0}</span>
                        </div>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="flex items-center gap-1 text-white font-bold text-sm">
                            <Heart className="w-4 h-4 fill-white" />
                            {look.likes?.length || 0}
                          </span>
                        </div>
                      </button>
                      {/* Edit/Delete menu for own looks */}
                      {isOwnProfile && (
                        <div className="absolute top-1.5 left-1.5 z-10">
                          <button
                            data-testid={`look-menu-${look.look_id}`}
                            onClick={(e) => { e.stopPropagation(); setLookMenuOpen(lookMenuOpen === look.look_id ? null : look.look_id); }}
                            className="p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                          >
                            <MoreVertical className="w-3.5 h-3.5 text-white" />
                          </button>
                          {lookMenuOpen === look.look_id && (
                            <div className="absolute left-0 top-7 bg-white rounded-xl shadow-lg border border-border py-1 min-w-[130px] z-20">
                              <button
                                data-testid={`edit-look-${look.look_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingLook(look);
                                  setEditCaption(look.caption || '');
                                  setLookMenuOpen(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                Edit Caption
                              </button>
                              <button
                                data-testid={`delete-look-${look.look_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingLook(look);
                                  setLookMenuOpen(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete Look
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
          </div>

          <div className={`transition-opacity duration-200 ${activeTab === 'saved' ? 'opacity-100' : 'hidden opacity-0'}`}>
          {activeTab === 'saved' && isOwnProfile && (
            savedLooks.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <Bookmark className="w-10 h-10 text-muted-foreground/40 mx-auto" />
                <p className="text-muted-foreground">No saved looks yet</p>
                <p className="text-sm text-muted-foreground">Save looks from Spotlight for later!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {savedLooks.map(look => {
                  const isBA = look.theme === 'Before & After';
                  const firstImage = isBA ? look.after_image : look.images?.[0];
                  return (
                    <button
                      key={look.look_id}
                      data-testid={`saved-look-${look.look_id}`}
                      onClick={() => navigate(`/spotlight?look=${look.look_id}`)}
                      className="relative aspect-square rounded-xl overflow-hidden group"
                    >
                      {firstImage ? (
                        <img src={firstImage} alt="" className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <span className="text-white text-xs font-medium">@{look.username}</span>
                        <span className="text-white/70 text-xs ml-1">· {look.theme}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          )}
          </div>

          {activeTab === 'checklists' && (
            profileData.checklists?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No checklists yet</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {profileData.checklists?.map(checklist => (
                  <div 
                    key={checklist.checklist_id} 
                    className="checklist-card rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/shared/checklist/${checklist.checklist_id}`)}
                    data-testid={`checklist-card-${checklist.checklist_id}`}
                  >
                    <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {checklist.name}
                    </h3>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      <span className="px-2 py-1 bg-white/50 rounded-full">{{ skincare: 'Face Care', makeup: 'Make Up', haircare: 'Hair Care', pharmacy: 'Pharma' }[checklist.filters?.category] || checklist.filters?.category || 'Face Care'}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{checklist.products?.length || 0} products</p>
                  </div>
                ))}
              </div>
            )
          )}
              </>
            );
          })()}
        </div>
          </>
        ) : (
          /* Follow to see content */
          <div className="text-center py-16 space-y-4" data-testid="follow-to-see">
            <Lock className="w-12 h-12 text-muted-foreground/40 mx-auto" />
            <p className="text-lg font-medium text-foreground">Follow to see their posts</p>
            <p className="text-sm text-muted-foreground">Once they accept your follow, you'll be able to see their posts, looks, and checklists.</p>
          </div>
        )}

        {/* Edit Look Modal */}
        {editingLook && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={() => setEditingLook(null)}>
            <div className="bg-white w-full max-w-md mx-4 rounded-2xl p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Look</h3>
                <button onClick={() => setEditingLook(null)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-edit-look">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <label className="block text-sm font-medium text-foreground mb-1">Caption</label>
              <textarea
                data-testid="edit-look-caption"
                value={editCaption}
                onChange={e => setEditCaption(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-xl text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Update your caption..."
              />
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditingLook(null)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  data-testid="save-edit-look"
                  onClick={handleEditLook}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Look Confirmation */}
        {deletingLook && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={() => setDeletingLook(null)}>
            <div className="bg-white w-full max-w-sm mx-4 rounded-2xl p-6 text-center" onClick={e => e.stopPropagation()}>
              <Trash2 className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Look?</h3>
              <p className="text-sm text-muted-foreground mb-4">This action cannot be undone. Your look will be permanently removed.</p>
              <div className="flex gap-2">
                <button onClick={() => setDeletingLook(null)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  data-testid="confirm-delete-look"
                  onClick={() => handleDeleteLook(deletingLook.look_id)}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post Detail Modal */}
        {selectedPost && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center" onClick={() => setSelectedPost(null)}>
            <div className="bg-white w-full h-[100dvh] sm:h-auto sm:max-h-[92vh] sm:max-w-lg sm:rounded-2xl sm:mt-8 overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between z-10">
                <h3 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Post</h3>
                <button onClick={() => setSelectedPost(null)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-post-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <PostCard 
                post={selectedPost}
                currentUser={currentUser}
                onLike={handleLike}
                onPostUpdate={() => {
                  setSelectedPost(null);
                  fetchProfile();
                }}
              />
            </div>
          </div>
        )}

        {/* Followers / Following Modal */}
        {showFollowModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center" onClick={() => { setShowFollowModal(null); setFollowList([]); }}>
            <div className="bg-white w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {showFollowModal === 'followers' ? 'Followers' : 'Following'}
                </h3>
                <button onClick={() => { setShowFollowModal(null); setFollowList([]); }} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-follow-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-4">
                {followListLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : followList.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {showFollowModal === 'followers' ? 'No followers yet' : 'Not following anyone'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {followList.map(user => (
                      <div key={user.user_id} className="flex items-center gap-3" data-testid={`follow-list-item-${user.user_id}`}>
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-11 h-11 rounded-full object-cover shrink-0 cursor-pointer" onClick={() => { setShowFollowModal(null); setFollowList([]); navigate(`/profile/${user.user_id}`); }} />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold shrink-0 cursor-pointer" onClick={() => { setShowFollowModal(null); setFollowList([]); navigate(`/profile/${user.user_id}`); }}>
                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setShowFollowModal(null); setFollowList([]); navigate(`/profile/${user.user_id}`); }}>
                          <p className="font-semibold text-sm truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">@{user.username || user.name?.replace(/\s+/g, '').toLowerCase()}</p>
                        </div>
                        {isOwnProfile && showFollowModal === 'followers' && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            {!user.is_following_back && !user.has_pending_request && (
                              <button
                                data-testid={`follow-back-${user.user_id}`}
                                onClick={() => handleFollowBack(user.user_id)}
                                className="px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                              >
                                Follow
                              </button>
                            )}
                            {!user.is_following_back && user.has_pending_request && (
                              <span
                                data-testid={`requested-${user.user_id}`}
                                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-muted-foreground rounded-lg"
                              >
                                Requested
                              </span>
                            )}
                            <button
                              data-testid={`remove-follower-${user.user_id}`}
                              onClick={() => handleRemoveFollower(user.user_id)}
                              className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {isOwnProfile && showFollowModal === 'following' && (
                          <button
                            data-testid={`unfollow-from-list-${user.user_id}`}
                            onClick={() => handleUnfollowFromList(user.user_id)}
                            className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors shrink-0"
                          >
                            Unfollow
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                </div>
                <div className="h-8 shrink-0" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
